import React from "react";
import { StandardPricingInputModel } from "../models";
import {
  CustomLabel,
  FlexContainer,
  InfoIcon,
  Input,
  nameof,
} from "@mindmemobile/mm-shared-global";
import { DescriptionCell, LabelCell } from "../styles";
import { AccountSettingsStore } from "../store";

type IProps = {
  standardPricing: StandardPricingInputModel;
  onUpdate: (data: StandardPricingInputModel) => void;
  renderAsLabels?: boolean;
};

export default class StandardPricing extends React.Component<IProps> {
  formatAmount = (value: string): string => {
    const sanitized = AccountSettingsStore.sanitizeAmount(value.toString());
    if (parseFloat(sanitized) < 0 || isNaN(parseFloat(sanitized))) {
      return "0.00";
    }
    return AccountSettingsStore.formatAmountWithoutCommas(
      parseFloat(sanitized || "0.00"),
      true,
    );
  };

  onChange(
    value: string | boolean,
    key: keyof StandardPricingInputModel,
    isBlur?: boolean,
  ) {
    const clonedObject = new StandardPricingInputModel({
      ...this.props.standardPricing,
    });

    switch (key) {
      case nameof<StandardPricingInputModel>("price"):
        if (isBlur) {
          value = this.formatAmount(value as string);
        }
        clonedObject.price = value.toString();
        break;
      case nameof<StandardPricingInputModel>("originalPrice"):
        if (isBlur) {
          value = this.formatAmount(value as string);
        }
        clonedObject.originalPrice = value.toString();
        break;
      case nameof<StandardPricingInputModel>("cost"):
        if (isBlur) {
          value = this.formatAmount(value as string);
        }
        clonedObject.cost = value.toString();
        break;
      case nameof<StandardPricingInputModel>("showOriginalPrice"):
        if (isBlur) {
          value = this.formatAmount(value as string);
        }
        clonedObject.showOriginalPrice = Boolean(value);
        break;
      case nameof<StandardPricingInputModel>("setUpFee"):
        if (isBlur) {
          value = this.formatAmount(value as string);
        }
        clonedObject.setUpFee = value.toString();
        break;
    }

    this.props.onUpdate(new StandardPricingInputModel(clonedObject));
  }

  hasError(fieldName: keyof StandardPricingInputModel): boolean {
    const error = this.props.standardPricing.errors?.find(
      (f) => f.field === fieldName,
    );
    return error !== undefined;
  }

  input(
    value: string,
    fieldName: keyof StandardPricingInputModel,
    placeHolder?: string,
  ) {
    return (
      <FlexContainer spacing={15} fxLayout="row" className="dollar-usd-input">
        <div>
          <Input
            value={value}
            error={this.hasError(nameof<StandardPricingInputModel>(fieldName))}
            id={`txt_${fieldName}`}
            type="text"
            placeholder={placeHolder}
            onChange={(e, isBlur) =>
              this.onChange(e.target.value, fieldName, isBlur)
            }
          />
        </div>
      </FlexContainer>
    );
  }

  checkBox(
    value: boolean,
    fieldName: keyof StandardPricingInputModel,
    label: string,
  ) {
    return (
      <Input
        value={value}
        error={this.hasError(nameof<StandardPricingInputModel>(fieldName))}
        id={`chk${fieldName}`}
        type="checkbox"
        label={label}
        onChange={(e) => this.onChange(e.target.checked, fieldName)}
      />
    );
  }

  render(): React.ReactNode {
    const { standardPricing, renderAsLabels } = this.props;

    const showCost = !renderAsLabels || standardPricing.cost;
    return (
      <>
        {standardPricing.showSetUpFee && (
          <FlexContainer className="label-description-row">
            <LabelCell>
              <CustomLabel lineHeight="40px" fontSize="16px">
                {" "}
                Setup Fee
              </CustomLabel>
              <InfoIcon />
            </LabelCell>
            <DescriptionCell>
              {!renderAsLabels &&
                this.input(
                  standardPricing.setUpFee,
                  nameof<StandardPricingInputModel>("setUpFee"),
                  "0.00",
                )}
              {renderAsLabels && (
                <CustomLabel>
                  {standardPricing.setUpFee
                    ? `$${AccountSettingsStore.formatAmount(parseFloat(standardPricing.setUpFee), true)}`
                    : "-"}
                </CustomLabel>
              )}
            </DescriptionCell>
          </FlexContainer>
        )}
        <FlexContainer className="label-description-row">
          <LabelCell>
            <CustomLabel lineHeight="40px" fontSize="16px">
              {" "}
              Price
            </CustomLabel>
            <InfoIcon />
          </LabelCell>
          <DescriptionCell className="price-field-ss">
            <FlexContainer spacing={15}>
              <div>
                {!renderAsLabels &&
                  this.input(
                    standardPricing.price,
                    nameof<StandardPricingInputModel>("price"),
                    "0.00",
                  )}

                {renderAsLabels && (
                  <CustomLabel>
                    $
                    {AccountSettingsStore.formatAmount(
                      parseFloat(standardPricing.price),
                      true,
                    )}
                  </CustomLabel>
                )}
              </div>
              {!renderAsLabels &&
                standardPricing.price &&
                standardPricing.cost &&
                this.checkBox(
                  standardPricing.showOriginalPrice,
                  nameof<StandardPricingInputModel>("showOriginalPrice"),
                  "Show Original Price",
                )}
            </FlexContainer>
          </DescriptionCell>
        </FlexContainer>
        {standardPricing.showOriginalPrice &&
          standardPricing.price &&
          standardPricing.cost && (
            <FlexContainer className="label-description-row">
              <LabelCell>
                <CustomLabel lineHeight="40px" fontSize="16px">
                  {" "}
                  Original Price
                </CustomLabel>
                <InfoIcon />
              </LabelCell>
              <DescriptionCell>
                {!renderAsLabels &&
                  this.input(
                    standardPricing.originalPrice,
                    nameof<StandardPricingInputModel>("originalPrice"),
                    "0.00",
                  )}
                {renderAsLabels && (
                  <CustomLabel>
                    $
                    {AccountSettingsStore.formatAmount(
                      parseFloat(standardPricing.originalPrice),
                      true,
                    )}
                  </CustomLabel>
                )}
              </DescriptionCell>
            </FlexContainer>
          )}

        {showCost && (
          <FlexContainer className="label-description-row">
            <LabelCell>
              <CustomLabel lineHeight="40px" fontSize="16px">
                Cost
              </CustomLabel>
              <InfoIcon />
            </LabelCell>
            <DescriptionCell>
              {!renderAsLabels &&
                this.input(
                  standardPricing.cost,
                  nameof<StandardPricingInputModel>("cost"),
                )}
              {renderAsLabels && (
                <CustomLabel>
                  {standardPricing.cost
                    ? `$${AccountSettingsStore.formatAmount(parseFloat(standardPricing.cost), true)}`
                    : "-"}
                </CustomLabel>
              )}
            </DescriptionCell>
          </FlexContainer>
        )}
      </>
    );
  }
}
